import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";

const MESSAGE_SLOTS = [
  { top: 18, left: 69, rotate: -2, align: "left" },
  { top: 43, left: 7, rotate: -4, align: "left" },
  { top: 66, left: 52, rotate: 2, align: "left" },
  { top: 27, left: 18, rotate: 3, align: "left" },
  { top: 51, left: 72, rotate: -3, align: "left" },
];

const MESSAGE_PAGE_INTERVAL_MS = 14000;

const getVisibleMessageCount = () => {
  if (typeof window === "undefined") return 4;

  const { innerWidth: width, innerHeight: height } = window;

  if (width < 640 || height < 560) return 2;
  if (width < 900 || height < 720) return 3;
  if (width < 1280 || height < 850) return 4;
  return 5;
};

const getMessageScale = (message = "") => {
  const length = message.length;

  if (length > 320) {
    return {
      fontSize: "clamp(0.58rem, 0.78vw, 0.92rem)",
      lineHeight: 1.08,
      width: "clamp(260px, 34vw, 560px)",
    };
  }

  if (length > 220) {
    return {
      fontSize: "clamp(0.68rem, 0.9vw, 1.05rem)",
      lineHeight: 1.1,
      width: "clamp(250px, 31vw, 520px)",
    };
  }

  if (length > 140) {
    return {
      fontSize: "clamp(0.8rem, 1.08vw, 1.2rem)",
      lineHeight: 1.12,
      width: "clamp(240px, 28vw, 470px)",
    };
  }

  if (length > 80) {
    return {
      fontSize: "clamp(0.92rem, 1.25vw, 1.4rem)",
      lineHeight: 1.14,
      width: "clamp(230px, 25vw, 420px)",
    };
  }

  return {
    fontSize: "clamp(1.05rem, 1.45vw, 1.65rem)",
    lineHeight: 1.16,
    width: "clamp(220px, 23vw, 390px)",
  };
};

const FloatingMessages = ({ userId, eventId }) => {
  const [messages, setMessages] = useState([]);
  const [visibleCount, setVisibleCount] = useState(getVisibleMessageCount);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!userId || !eventId) return;

    const q = query(
      collection(db, "users", userId, "events", eventId, "messages"),
      orderBy("timestamp", "desc")
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((message) => message.moderationStatus !== "needsReview");
      setMessages(data);
      setPage(0);
    });

    return () => unsub();
  }, [userId, eventId]);

  useEffect(() => {
    const updateVisibleCount = () => {
      setVisibleCount(getVisibleMessageCount());
      setPage(0);
    };

    updateVisibleCount();
    window.addEventListener("resize", updateVisibleCount);
    return () => window.removeEventListener("resize", updateVisibleCount);
  }, []);

  useEffect(() => {
    if (messages.length <= visibleCount) return undefined;

    const interval = window.setInterval(() => {
      setPage(
        (current) => (current + 1) % Math.ceil(messages.length / visibleCount)
      );
    }, MESSAGE_PAGE_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [messages.length, visibleCount]);

  const visibleMessages = useMemo(() => {
    if (!messages.length) return [];

    const start = (page * visibleCount) % messages.length;
    return Array.from(
      { length: Math.min(visibleCount, messages.length) },
      (_, i) => {
        const index = (start + i) % messages.length;
        return messages[index];
      }
    );
  }, [messages, page, visibleCount]);

  return (
    <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
      {visibleMessages.map((msg, i) => {
        const { top, left, rotate, align } =
          MESSAGE_SLOTS[i % MESSAGE_SLOTS.length];
        const messageScale = getMessageScale(msg.message);

        return (
          <div
            key={`${msg.id || msg.timestamp?.seconds || i}-${page}`}
            className="absolute select-none font-bold text-white drop-shadow-lg transition-opacity duration-700"
            style={{
              top: `${top}%`,
              left: `${left}%`,
              transform: `rotate(${rotate}deg)`,
              width: messageScale.width,
              fontSize: messageScale.fontSize,
              lineHeight: messageScale.lineHeight,
              opacity: 0.34,
              textAlign: align,
              overflowWrap: "anywhere",
            }}
          >
            <div>{msg.message}</div>
            <p className="mt-1 text-xs text-white opacity-75 sm:text-sm">
              - {msg.name || "Anonymous"}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default FloatingMessages;
